import React, { FC, useState, useEffect, ChangeEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { Select, ISelectOption } from 'components/forms/Selects'
import { Autocomplete } from 'components/forms/autocomplete/DPLAutocomplete'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { Message } from 'transfix-ui/components/GlobalMessaging'
import { LogoTransfix } from 'transfix-ui/components/LogoTransfix'
import { PhoneInput } from 'transfix-ui/components/PhoneInput'
import { USER_ROLES as USER_ROLES_ENUM } from 'types'
import { MESSAGE_TYPES } from 'types/globalMessaging'
import {
  searchUsers,
  createUser,
  createChildUser,
  updateParentUserProfileDetails,
  clearErrorMessages,
} from 'utils/actions/user'
import { ExistingUserForm } from 'apps/user/components/UserCreate/ExistingUserForm'
import {
  PERMITTED_USER_CREATION_TYPES,
  USER_ROLES,
  USER_ROLE_DESCRIPTION,
} from 'apps/user/constants/user'
import {
  IOrgDetails,
  IUser,
  IChildUser,
  ISearchedUser,
  IUserCreateStore,
  IUserCreationState,
  USER_TYPES,
} from 'apps/user/types/user'
import { validateParentUser, validateChildUser } from 'apps/user/utils/validator'
import { TextInput } from 'components/atoms'
import { getUserCookieValues } from 'utils/userAuth'
import { userGroupBooleans } from 'utils/userTypes'
import { validateEmail } from 'utils/validators'
import { USER_TYPE } from 'constants/userTypes'
import { getUrlParams } from '../utils'

const {
  applicationId: storedApplicationId,
  applicationType: storedApplicationType,
  userType: storedUserType,
} = getUserCookieValues() as {
  userType: USER_TYPE
  applicationType: 'ShipperApplication' | 'CarrierApplication'
  applicationId: string
}
const getPermittedUserTypes = () => {
  const userGroup = userGroupBooleans(storedUserType)
  if (userGroup.isAdmin) return PERMITTED_USER_CREATION_TYPES.admin
  if (userGroup.isCarrier) return PERMITTED_USER_CREATION_TYPES.carrier
  return PERMITTED_USER_CREATION_TYPES.shipper
}

interface IOrgDetailsProps {
  details: Partial<IOrgDetails>
  isAdmin: boolean
}

const OrgDetails: FC<IOrgDetailsProps> = ({ details, isAdmin }) => {
  const carrierLink = isAdmin ? `/carriers/${details?.id}` : `${__ROOT__}/carriers/${details?.id}`
  const shipperLink = isAdmin ? `/shippers/${details?.id}` : `${__ROOT__}/shippers/${details?.id}`

  return (
    <div className='card transparent bg-secondary'>
      {details.type === 'carrier' ? (
        <>
          <p>
            <a target='_blank' rel='noopener noreferrer' href={carrierLink}>
              <strong>{details?.name}</strong>
            </a>
          </p>
          {details?.mcNumber && (
            <p>
              <strong>MC: </strong>
              {details?.mcNumber}
            </p>
          )}
          {details?.dotNumber && (
            <p>
              <strong>DOT: </strong>
              {details?.dotNumber}
            </p>
          )}
        </>
      ) : (
        <p>
          <a target='_blank' rel='noopener noreferrer' href={shipperLink}>
            <strong>{details?.name}</strong>
          </a>
        </p>
      )}
    </div>
  )
}

const setOrgAndOrgDetails = (
  search: string,
  userType: string,
  data: Partial<IUser>,
  setOrg: (orgName: string) => void,
  setOrgDetails: (orgDetails: { [key: string]: string }) => void,
  setData: (data: Partial<IUser>) => void
): void => {
  const { orgDotNumber, orgId, orgMcNumber, orgName, orgType } = getUrlParams(search)
  const resetOrgDetails = (): void => {
    setOrg('')
    setOrgDetails({})
    setData({
      ...data,
      applicationId: undefined,
      applicationType: undefined,
    })
  }

  switch (userType) {
    case 'shipper': {
      if (orgType === 'shipper') {
        setOrg(orgName)
        setOrgDetails({
          type: orgType,
          id: orgId,
          name: orgName,
        })
        setData({
          ...data,
          applicationId: orgId,
          applicationType: 'ShipperApplication',
        })
      } else {
        resetOrgDetails()
      }
      break
    }
    case 'dispatcher':
    case 'driver':
    case 'owner_operator': {
      if (orgType === 'carrier') {
        setOrg(orgName)
        setOrgDetails({
          type: orgType,
          id: orgId,
          name: orgName,
          mcNumber: orgMcNumber,
          dotNumber: orgDotNumber,
        })
        setData({
          ...data,
          applicationId: orgId,
          applicationType: 'CarrierApplication',
        })
      } else {
        resetOrgDetails()
      }
      break
    }
    default:
      resetOrgDetails()
      break
  }
}

export const UserCreate = () => {
  // interaction state
  const [showEmail, setShowEmail] = useState<boolean>(false)
  const [showPhone, setShowPhone] = useState<boolean>(false)
  const [performedUserSearch, setPerformedUserSearch] = useState<boolean>(false)

  const [showNewUserForm, setShowNewUserForm] = useState<boolean>(false)
  const [showGlobalErrorMessage, setShowGlobalErrorMessage] = useState<boolean>(false)

  // note:  existing user creation support
  const [showExistingUserForm, setShowExistingUserForm] = useState<boolean>(false)
  const [userRequiresUpdate, setUserRequiresUpdate] = useState<boolean>(false)

  const [showCarrierSearch, setShowCarrierSearch] = useState<boolean>(!storedApplicationId)
  const [showShipperSearch, setShowShipperSearch] = useState<boolean>(!storedApplicationId)

  const [showCellPhone, setShowCellPhone] = useState<boolean>(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // state from params
  const { search } = useLocation()

  // local data state
  const [userType, setUserType] = useState<USER_TYPES>()
  const [role, setRole] = useState<USER_ROLES_ENUM>()
  const [org, setOrg] = useState<string>('')
  const [orgDetails, setOrgDetails] = useState<Partial<IOrgDetails>>()
  const [data, setData] = useState<Partial<IUser>>({
    applicationId: storedApplicationId,
    applicationType: storedApplicationType,
    confirmed: { value: false },
  })

  // note: admin check for existing user creation support
  const isExistingUserExtensionSupported = userGroupBooleans(storedUserType).isAdmin

  // state management for network events
  const dispatch = useDispatch()
  const {
    isCreatingUser,
    isSearchingUser,
    searchedUsers,
    user,
    userCreateError,
    userSearchError,
  } = useSelector<IUserCreateStore, IUserCreationState>(({ userCreate }) => ({ ...userCreate }))

  useEffect(() => {
    if (search && userType) {
      setOrgAndOrgDetails(search, userType, data, setOrg, setOrgDetails, setData)
    }
  }, [search, userType])

  useEffect(() => {
    if (performedUserSearch && searchedUsers?.length === 0) {
      setShowNewUserForm(true)
    }
    if (performedUserSearch && searchedUsers && searchedUsers.length > 0) {
      if (isExistingUserExtensionSupported) {
        const { cellPhone, id, name, phone, phoneExt } = searchedUsers[0]
        const [firstName, lastName] = name.split(' ')

        setShowExistingUserForm(true)

        // note: override checked in contact data with retrieved data, apart from
        // names
        setData({
          firstName,
          lastName,

          ...data,

          cellPhone,
          parentUserId: id,
          phone,
          phoneExt,
        })
      } else {
        setShowGlobalErrorMessage(true)
      }
    }
  }, [performedUserSearch, searchedUsers])

  useEffect(() => {
    const { isAdmin, isCarrier, isShipper } = userGroupBooleans((userType as string) as USER_TYPE)

    setShowCarrierSearch(isCarrier && !storedApplicationId)
    setShowShipperSearch(isShipper && !storedApplicationId)
    setShowCellPhone((isCarrier || isAdmin) && userType !== 'driver')
  }, [userType])

  const updateExistingUser = (): void => {
    setUserRequiresUpdate(false)
    const { parentUserId } = user as IChildUser
    const { cellPhone, firstName, lastName, phone } = data
    const newUser = { id: parentUserId, firstName, lastName, phone, cellPhone }
    dispatch(updateParentUserProfileDetails(newUser))
  }

  useEffect(() => {
    if (user && user.id) {
      if (userRequiresUpdate) {
        updateExistingUser()
        global.location.href = `${__ROOT__}/users/${user.parentUserId}`
      } else {
        global.location.href = `${__ROOT__}/users/${
          isExistingUserExtensionSupported ? `${user.parentUserId}` : user.id
        }`
      }
    }
  }, [user])

  const searchUser = (): void => {
    const searchTerm: string = (showEmail ? data?.email : data?.cellPhone) || ''

    if (showEmail && !validateEmail(data?.email)) {
      setErrors({
        email: 'Use a valid email address',
      })
      return
    }

    if (!data?.cellPhone && userType === 'driver') {
      setErrors({
        cellPhone: 'Field is required',
      })
      return
    }
    // note, the second argument here determines if we search by `users` or
    // `parents`
    dispatch(searchUsers(searchTerm, isExistingUserExtensionSupported))
    setPerformedUserSearch(true)
  }

  const saveUser = (): void => {
    const payload = { ...data, userType, role }

    try {
      validateParentUser(payload)
      dispatch(createUser(payload))
    } catch (errs) {
      setErrors(errs)
    }
  }

  const doesUserRequireUpdate = (existingUser: ISearchedUser, newUser: Partial<IUser>) => {
    const newUserName = `${newUser?.firstName} ${newUser?.lastName}`

    const areNamesDifferent = newUserName.toLowerCase() !== existingUser.name.toLowerCase()
    const arePhonesDifferent = newUser.phone !== existingUser.phone
    const areCellPhonesDifferent = newUser.cellPhone !== existingUser.cellPhone
    return areNamesDifferent || arePhonesDifferent || areCellPhonesDifferent
  }

  const saveChildUser = (): void => {
    const payload = { ...data, userType, role }
    setUserRequiresUpdate(doesUserRequireUpdate(searchedUsers![0], data))

    try {
      validateChildUser(payload)
      dispatch(createChildUser(payload))
    } catch (errs) {
      setErrors(errs)
    }
  }

  const inputChangeHandler = ({ target: { name, value } }: ChangeEvent<HTMLInputElement>): void => {
    const newKeyValuePair: { [key: string]: string } = {}
    newKeyValuePair[name] = value
    setData({ ...data, ...newKeyValuePair })
  }

  const handleUserTypeSelection = (selectedUserType: ISelectOption<USER_TYPES>): void => {
    // reset form field displayed
    setShowPhone(false)
    setShowEmail(false)
    setShowNewUserForm(false)
    setShowExistingUserForm(false)
    setErrors({})

    setUserType(selectedUserType.value)
    setOrg('')
    setOrgDetails({})

    const type = selectedUserType.value
    // set form field displayed
    if (type === 'driver') {
      setRole(USER_ROLES_ENUM.employee)
      setShowPhone(true)
    } else {
      setShowEmail(true)
    }
  }

  const autoCompleteChangeHandler = ({
    fullSelection: { dotNumber, id, mcNumber, name, type },
  }: {
    fullSelection: IOrgDetails
  }): void => {
    const orgFields: { [key: string]: string | number } = {}
    orgFields.applicationId = id as number
    orgFields.applicationType = type === 'carrier' ? 'CarrierApplication' : 'ShipperApplication'

    setOrgDetails({
      id,
      dotNumber,
      mcNumber,
      name,
      type,
    })
    setOrg(name as string)
    setData({ ...data, ...orgFields })
  }

  const handleRoleSelection = (selectedRole: ISelectOption<USER_ROLES_ENUM>): void => {
    setRole(selectedRole.value)
  }

  const toggleUserForm = (): void => {
    setShowNewUserForm(!showNewUserForm)
    setErrors({})
  }

  const toggleExistingUserForm = (): void => {
    setShowExistingUserForm(!showExistingUserForm)
    setErrors({})
  }

  const toggleGlobalErrorMessage = (): void => setShowGlobalErrorMessage(!showGlobalErrorMessage)
  const clearUserCreationErrorMessages = (): void => {
    dispatch(clearErrorMessages)
  }

  const getFormAction: () => { text: string; disabled: boolean; action: () => void } = () => {
    if (showNewUserForm) return { text: 'Add User', disabled: isCreatingUser, action: saveUser }
    if (showExistingUserForm)
      return { text: 'Save Changes', disabled: isCreatingUser, action: saveChildUser }
    return { text: 'Next', disabled: isSearchingUser, action: searchUser }
  }

  const formAction = getFormAction()

  const { isAdmin } = userGroupBooleans(storedUserType)

  return (
    <>
      {showGlobalErrorMessage && (
        <Message
          id='user-exists-message'
          type={MESSAGE_TYPES.alert}
          message={`A user with that ${
            showEmail ? 'email address' : 'phone number'
          } already exists`}
          hideMessage={toggleGlobalErrorMessage}
          cta={{
            label: 'View User Profile',
            url: `${__ROOT__}/users/${
              searchedUsers && searchedUsers.length ? searchedUsers[0].id : ''
            }`,
          }}
        />
      )}
      {(userCreateError || userSearchError) && (
        <Message
          id={`user-${userCreateError ? 'create' : 'search'}-error-message`}
          type={MESSAGE_TYPES.alert}
          message={(userCreateError || userSearchError) as string}
          hideMessage={clearUserCreationErrorMessages}
          cta={{
            label: 'Try again',
            action: showNewUserForm ? saveUser : searchUser,
          }}
        />
      )}
      <div className='container col-xs-12 col-md-6 md-shift-3 col-lg-4 mt8 lg-shift-4'>
        <Card>
          <div className='ml2'>
            <div className='logo'>
              <LogoTransfix color='black' />
            </div>
            <h1 className='sub-heading mb5'>Create a user</h1>
          </div>

          <div>
            <div className='col-xs-12 ma0 mb4'>
              <Select
                large
                required
                label='User Type'
                placeholder='Select a user type'
                value={userType}
                onChange={handleUserTypeSelection}
                options={getPermittedUserTypes()}
              />
            </div>

            {!showExistingUserForm && (showEmail || showPhone) && (
              <div className='col-xs-12 ma0 mb4'>
                {showEmail && (
                  <>
                    {!showNewUserForm ? (
                      <TextInput
                        large
                        required
                        label='Email'
                        placeholder='Enter email address'
                        value={data?.email}
                        name='email'
                        invalid={!!errors?.email}
                        validationMessage={errors?.email}
                        onChange={inputChangeHandler}
                      />
                    ) : (
                      <>
                        <p className='text-caption required'>Email</p>
                        <p>{data?.email}</p>
                        <p
                          className='btn-text-link tl'
                          tabIndex={0}
                          role='button'
                          onKeyDown={toggleUserForm}
                          onClick={toggleUserForm}>
                          <strong>Choose a different email</strong>
                        </p>
                      </>
                    )}
                  </>
                )}
                {showPhone && (
                  <>
                    {!showNewUserForm ? (
                      <PhoneInput
                        large
                        required
                        label='Cell phone'
                        value={data?.cellPhone}
                        name='cellPhone'
                        validationMessage={errors?.cellPhone}
                        onChange={inputChangeHandler}
                      />
                    ) : (
                      <>
                        <p className='text-caption required'>Cell phone</p>
                        <p>{data?.cellPhone}</p>
                        <p
                          className='btn-text-link tl'
                          tabIndex={0}
                          role='button'
                          onKeyDown={toggleUserForm}
                          onClick={toggleUserForm}>
                          <strong>Choose a different cell phone number</strong>
                        </p>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {!showExistingUserForm && (
              <>
                <div className='col-xs-12 ma0 mb4'>
                  <TextInput
                    large
                    required
                    label='First name'
                    placeholder='Johnny'
                    value={data?.firstName}
                    name='firstName'
                    onChange={inputChangeHandler}
                    validationMessage={errors?.firstName}
                    invalid={!!errors?.firstName}
                  />
                </div>
                <div className='col-xs-12 ma0 mb4'>
                  <TextInput
                    large
                    required
                    label='Last name'
                    placeholder='Appleseed'
                    value={data?.lastName}
                    name='lastName'
                    onChange={inputChangeHandler}
                    validationMessage={errors?.lastName}
                    invalid={!!errors?.lastName}
                  />
                </div>
              </>
            )}

            {showNewUserForm && (
              <>
                {showEmail && (
                  <div className='col-xs-12 ma0 pa0 mb4'>
                    <div className='col-xs-7'>
                      <PhoneInput
                        large
                        required
                        label='Phone'
                        value={data?.phone}
                        name='phone'
                        onChange={inputChangeHandler}
                        validationMessage={errors?.phone}
                        invalid={!!errors?.phone}
                      />
                    </div>
                    <div className='col-xs-4'>
                      <TextInput
                        large
                        label='Ext'
                        placeholder='0000'
                        value={data?.phoneExt}
                        inputMode='numeric'
                        maxLength={4}
                        validationMessage={errors?.phoneExt}
                        invalid={!!errors?.phoneExt}
                        name='phoneExt'
                        onChange={inputChangeHandler}
                      />
                    </div>
                  </div>
                )}
                {showCellPhone && (
                  <div className='mb4 col-xs-7'>
                    <PhoneInput
                      large
                      required={userType !== 'dispatcher'}
                      value={data?.cellPhone}
                      label='Cell phone'
                      name='cellPhone'
                      onChange={inputChangeHandler}
                      validationMessage={errors?.cellPhone}
                      invalid={!!errors?.cellPhone}
                    />
                  </div>
                )}
              </>
            )}

            {showExistingUserForm && (
              <div className='col-xs-12 ma0 mb4'>
                <ExistingUserForm
                  data={data}
                  errors={errors}
                  existingUser={searchedUsers![0]}
                  inputChangeHandler={inputChangeHandler}
                  toggleForm={toggleExistingUserForm}
                  userType={userType as USER_TYPES}
                  showCellPhone={showCellPhone}
                  showEmail={showEmail}
                  showPhone={showPhone}
                />
              </div>
            )}

            {/* Org Settings and Role Settings */}
            {(showNewUserForm || showExistingUserForm) && (
              <>
                {showCarrierSearch && (
                  <>
                    <div className='col-xs-12 mb4'>
                      <Autocomplete
                        large
                        required
                        dataTest='search-carrier'
                        id='search-carrier'
                        label='Carrier'
                        onChange={autoCompleteChangeHandler}
                        placeholder='Search for a carrier'
                        type='carrier'
                        value={org}
                        validationMessage={errors?.applicationId}
                        invalid={!!errors?.applicationId}
                      />
                    </div>
                    <p className='col-xs-12 ml2 mb4 text-caption'>
                      Enter an MC or DOT number and select a carrier.
                    </p>
                  </>
                )}

                {showShipperSearch && (
                  <>
                    <div className='col-xs-12 mb4'>
                      <Autocomplete
                        large
                        required
                        dataTest='search-shipper'
                        id='search-ishipper'
                        label='Shipper'
                        onChange={autoCompleteChangeHandler}
                        placeholder='Search for a shipper'
                        type='shipper'
                        value={org}
                        validationMessage={errors?.applicationId}
                        invalid={!!errors?.applicationId}
                      />
                    </div>
                    <p className='col-xs-12 ml2 mb4 text-caption'>
                      Enter a company name and select a shipper.
                    </p>
                  </>
                )}

                {org && orgDetails && (
                  <div className='col-xs-12 mb4'>
                    <OrgDetails details={orgDetails} isAdmin={isAdmin} />
                  </div>
                )}

                <div className='row col-xs-12 mb4'>
                  {userType !== 'driver' ? (
                    <>
                      <div className='col-xs-8'>
                        <Select
                          large
                          required
                          label='Role'
                          value={role}
                          onChange={handleRoleSelection}
                          placeholder='Select a Role'
                          options={USER_ROLES}
                          validationMessage={errors?.role}
                          invalid={!!errors?.role}
                        />
                      </div>
                      <p className='col-xs-12 ml2 text-caption'>
                        {USER_ROLE_DESCRIPTION[role as string]}
                      </p>
                    </>
                  ) : (
                    <div className='col-xs-8'>
                      <p className='text-caption required'>Role</p>
                      <p>Employee</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Action Button */}
            {(showEmail || showPhone) && (
              <div className='col-xs-12 flex justify-end'>
                <Button disabled={formAction.disabled} onClick={formAction.action} color='primary'>
                  {formAction.text}
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    </>
  )
}
